import {
  EUserType,
  ICurso,
  IFiltroSerie,
  IMateria,
  IMensagem,
  IMensagemAtribuicao,
  ISerie,
  ITurma,
  utils,
  yupMensagemAtribuicao,
} from "@deltasge/marauders-map";
import {
  Button,
  Chip,
  Divider,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { Delete as DeleteIcon } from "mdi-material-ui";
import { useDialog } from "hooks/useDialog";
import { Plus as AddIcon } from "mdi-material-ui";
import React, { FC, useCallback, useState } from "react";
import { DialogFilterAtribuicao } from "./DialogFilterAtribuicao";
import { snack } from "components/GlobalSnackbar";
import { useAppSelector } from "store";

export const MensagemAtribuicoes: FC<{
  atribuicoes: IMensagemAtribuicao[];
  setValue: (key: keyof IMensagem, value: unknown) => void;
}> = ({ atribuicoes, setValue }) => {
  const {
    treeView: { cursos: todosOsCursos },
    anoLetivo: { anoLetivo },
    usuario: { usuario },
  } = useAppSelector((state) => state);

  let atrParaProfessor: IMensagemAtribuicao | undefined = undefined;
  if (usuario?.tipoUsuario == EUserType.Professor) {
    const cursos = utils.getCursosTreeView({
      cursos: todosOsCursos,
    });
    const idCurso = cursos.at(0)?.id || null;
    const curso = cursos.find((f) => f.id == idCurso) as ICurso | undefined;
    const idSerie = curso?.series.at(0)?.id || null;
    const serie = curso?.series.find((f) => f.id == idSerie) as
      | IFiltroSerie
      | undefined;
    const materias = utils.getMateriasTreeView({
      serie,
      sort: true,
    });
    const idTurma = serie?.turmas?.at(0)?.id || null;
    const turma = serie?.turmas?.find((f) => f.id == idTurma);
    const idMateria = materias.at(0)?.id || null;
    const materia = materias.find((f) => f.id == idMateria) as
      | IMateria
      | undefined;

    atrParaProfessor = {
      ...yupMensagemAtribuicao.getDefault(),
      anoLetivo: anoLetivo.toString(),
      aluno: true,
      idCurso,
      curso,
      idSerie,
      serie: serie as ISerie | undefined,
      idTurma,
      turma: turma as ITurma | undefined,
      idMateria,
      materia,
    };
  }

  const [ultima, setUltima] = useState<IMensagemAtribuicao | undefined>(
    atrParaProfessor
  );
  const { RenderDialog, show, hide } = useDialog({
    title: "Enviar para",
    options: {
      modal: true,
    },
  });

  const handleAddAtribuicao = (atr: IMensagemAtribuicao, close?: boolean) => {
    if (atribuicoes.length > 0) {
      const has = atribuicoes.find(
        (f) =>
          f.aluno == atr.aluno &&
          f.responsavel == atr.responsavel &&
          f.loginExtra == atr.loginExtra &&
          f.professor == atr.professor &&
          f.idCurso == atr.idCurso &&
          f.idSerie == atr.idSerie &&
          f.idTurma == atr.idTurma
      );
      if (has != undefined) {
        snack.warning("Atribuição já adicionada");
        return;
      }
    }
    atribuicoes.push(atr);
    setUltima(atr);
    setValue("mensagemAtribuicoes", atribuicoes);
    if (close) hide();
  };

  const handleRemoveAtribuicao = useCallback(
    (index: number) => {
      atribuicoes.splice(index, 1);
      setValue("mensagemAtribuicoes", atribuicoes);
    },
    [atribuicoes, setValue]
  );

  return (
    <TableContainer>
      <RenderDialog>
        <DialogFilterAtribuicao
          onClose={hide}
          addAtribuicao={handleAddAtribuicao}
          atribuicao={ultima}
        />
      </RenderDialog>
      <Toolbar>
        <Grid container justifyContent="space-between" alignItems="center">
          <Grid item xs="auto">
            <Typography variant="h6" component="div">
              Enviar para
            </Typography>
          </Grid>
          <Grid item>
            <Button
              color="secondary"
              size="small"
              disableElevation
              variant="contained"
              onClick={show}
              startIcon={<AddIcon />}
            >
              Adicionar
            </Button>
          </Grid>
        </Grid>
      </Toolbar>
      <Divider />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Quem irá receber</TableCell>
            <TableCell>Matéria</TableCell>
            <TableCell>Destinatário</TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {atribuicoes.map((m, i) => {
            let quem = "Todos os cursos";
            if (m.turma) {
              quem = m.turma.tituloTurma;
            } else if (m.serie) {
              quem = `Todas as turmas do ${m.serie.descricao.rUpper()}`;
            } else if (m.curso) {
              quem = `Todas as séries do ${m.curso.nome.rUpper()}`;
            }

            const {
              aluno,
              responsavel,
              loginExtra,
              professor,
              mensagemAtribuicaoAlunos: maa,
              mensagemAtribuicaoResponsaveis: mar,
              mensagemAtribuicaoLoginsExtras: male,
              mensagemAtribuicaoProfessores: map,
            } = m;

            let labelAluno = "Todos os alunos";
            let nomeAlunos: React.ReactNode | undefined;
            if (maa && maa.length > 0) {
              labelAluno = `${maa.length} aluno${maa.length > 1 ? "s" : ""}`;
              nomeAlunos = (
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {maa.map((m, i) => (
                    <li key={`aluno_${i}_${m.id}`}>
                      {`${m.aluno?.codigo} - ${
                        m.aluno?.nomeusual?.trim() || m.aluno?.nome
                      }`}
                    </li>
                  ))}
                </ul>
              );
            }

            let labelResponsavel = "Todos os responsáveis";
            let nomeResponsaveis: React.ReactNode | undefined;
            if (mar && mar.length > 0) {
              labelResponsavel = `${mar.length} responsáve${
                mar.length > 1 ? "is" : "l"
              }`;
              nomeResponsaveis = (
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {mar.map((m, i) => (
                    <li key={`responsavel_${i}_${m.id}`}>
                      {m.responsavel?.codigo} - {m.responsavel?.nome}
                    </li>
                  ))}
                </ul>
              );
            }

            let labelLoginExtra = "Todos os logins de alunos";
            let nomeLoginsExtra: React.ReactNode | undefined;
            if (male && male.length > 0) {
              labelLoginExtra = `${male.length} login${
                male.length > 1 ? "s" : ""
              }`;
              nomeLoginsExtra = (
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {male.map((m, i) => (
                    <li key={`login_extra_${i}_${m.id}`}>
                      {m.loginExtra?.codigo} - {m.loginExtra?.tipo} -{" "}
                      {m.loginExtra?.nome}
                    </li>
                  ))}
                </ul>
              );
            }

            let labelProfessor = "Todos os professores";
            let nomeProfessor: React.ReactNode | undefined;
            if (map && map.length > 0) {
              labelProfessor = `${map.length} professor${
                map.length > 1 ? "es" : ""
              }`;
              nomeProfessor = (
                <ul style={{ margin: 0, padding: 0, listStyle: "none" }}>
                  {map.map((m, i) => (
                    <li key={`login_extra_${i}_${m.id}`}>
                      {m.professor?.codigo} - {m.professor?.nome}
                    </li>
                  ))}
                </ul>
              );
            }

            return (
              <TableRow key={`atr_${i}`}>
                <TableCell>{quem}</TableCell>
                <TableCell>
                  {m.materia?.nome?.rUpper() || "Independente da matéria"}
                </TableCell>
                <TableCell>
                  <Grid container spacing={1}>
                    {aluno && (
                      <Grid item>
                        <Tooltip title={nomeAlunos}>
                          <Chip label={labelAluno} />
                        </Tooltip>
                      </Grid>
                    )}
                    {responsavel && (
                      <Grid item>
                        <Tooltip title={nomeResponsaveis}>
                          <Chip label={labelResponsavel} />
                        </Tooltip>
                      </Grid>
                    )}
                    {loginExtra && (
                      <Grid item>
                        <Tooltip title={nomeLoginsExtra}>
                          <Chip label={labelLoginExtra} />
                        </Tooltip>
                      </Grid>
                    )}
                    {professor && (
                      <Grid item>
                        <Tooltip title={nomeProfessor}>
                          <Chip label={labelProfessor} />
                        </Tooltip>
                      </Grid>
                    )}
                  </Grid>
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>
                  <IconButton
                    size="small"
                    color="secondary"
                    onClick={() => handleRemoveAtribuicao(i)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
